import { config } from './config';
import type { ArtifactName, Job } from './types';

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${config.apiUrl}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  if (!response.ok) {
    const message = await response
      .json()
      .then((body: { message?: string }) => body.message)
      .catch(() => undefined);
    throw new ApiError(response.status, message ?? `Request failed with ${response.status}`);
  }
  return (await response.json()) as T;
}

export function listJobs(token: string): Promise<{ jobs: Job[] }> {
  return request<{ jobs: Job[] }>(token, '/jobs');
}

export function getJob(token: string, jobId: string): Promise<Job> {
  return request<Job>(token, `/jobs/${jobId}`);
}

/** What the uploader knows about the meeting that the audio cannot tell us. */
export interface JobHints {
  participants?: string[];
  terms?: string[];
}

export function createJob(
  token: string,
  file: File,
  hints: JobHints = {},
): Promise<{ jobId: string; uploadUrl: string; contentType: string }> {
  return request(token, '/jobs', {
    method: 'POST',
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || 'audio/mpeg',
      sizeBytes: file.size,
      ...hints,
    }),
  });
}

function artifactUrl(token: string, jobId: string, artifact: ArtifactName) {
  return request<{ url: string; fileName: string }>(token, `/jobs/${jobId}/artifacts/${artifact}`);
}

export async function downloadArtifact(
  token: string,
  jobId: string,
  artifact: ArtifactName,
): Promise<void> {
  const { url } = await artifactUrl(token, jobId, artifact);
  // The presigned URL carries Content-Disposition: attachment, so a plain
  // navigation downloads the file instead of rendering it.
  window.location.assign(url);
}

/**
 * Same presigned URL, fetched instead of navigated to - the attachment
 * disposition only applies to a navigation, so this returns the text for
 * rendering in the app.
 */
export async function fetchArtifactText(
  token: string,
  jobId: string,
  artifact: ArtifactName,
): Promise<string> {
  const { url } = await artifactUrl(token, jobId, artifact);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Datei konnte nicht geladen werden (HTTP ${response.status})`);
  }
  return response.text();
}

export interface Todo {
  task: string;
  owner?: string;
  condition?: string | null;
  dueDate?: string | null;
  quote?: string;
  timestamp?: string | null;
}

/** The tasks as the pipeline recorded them, for the Deck preview to edit. */
export async function fetchTodos(token: string, jobId: string): Promise<Todo[]> {
  const text = await fetchArtifactText(token, jobId, 'todos-json');
  const parsed: unknown = JSON.parse(text);
  return Array.isArray(parsed) ? (parsed as Todo[]) : [];
}

export interface DeckBoard {
  id: number;
  title: string;
  stacks: { id: number; title: string }[];
  members: string[];
}

export function deckBoards(
  token: string,
): Promise<{ boards: DeckBoard[]; reason?: string; account?: string }> {
  return request(token, '/deck/boards');
}

export interface DeckCardInput {
  title: string;
  description?: string;
  assignUserId?: string;
  dueDate?: string;
}

export function exportToDeck(
  token: string,
  jobId: string,
  body: { boardId: number; stackId: number; cards: DeckCardInput[] },
): Promise<{ created: { title: string; id?: number; assigned?: boolean; failed?: boolean }[] }> {
  return request(token, `/jobs/${jobId}/deck`, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Uploads straight to S3 with the presigned URL. XHR rather than fetch because
 * we want an upload progress bar, which fetch still cannot give us.
 */
/**
 * How long the upload may make no progress at all before it is given up on.
 *
 * A stalled PUT fires no event of any kind - not `error`, not `timeout`, not
 * `load` - so without this the promise never settles: the panel keeps showing
 * "wird hochgeladen … 0 %" forever and the job stays in UPLOADING. That is
 * exactly what uploads picked from Google Drive on a phone did.
 */
const STALL_TIMEOUT_MS = 90_000;
const STALL_CHECK_MS = 5_000;

/**
 * Said in full rather than as "network error", because the cause is almost
 * always the same one and the remedy is not obvious: the file picker hands over
 * a reference to the Drive app, not the file, and reading it downloads it first.
 */
const STALL_MESSAGE =
  'Der Upload kam zum Stillstand. Bei Dateien aus Google Drive passiert das, wenn die Datei nur in der Cloud liegt: ' +
  'bitte in der Drive-App „Offline verfügbar machen“ wählen (oder die Datei herunterladen) und dann erneut hochladen.';

export function uploadToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lastActivity = Date.now();

    const stallCheck = window.setInterval(() => {
      if (Date.now() - lastActivity < STALL_TIMEOUT_MS) return;
      window.clearInterval(stallCheck);
      // Aborting fires `abort`, which would otherwise report "abgebrochen" as if
      // the person had cancelled - so the listener is removed first.
      xhr.removeEventListener('abort', onAbort);
      xhr.abort();
      reject(new Error(STALL_MESSAGE));
    }, STALL_CHECK_MS);

    const settle = (finish: () => void) => {
      window.clearInterval(stallCheck);
      finish();
    };
    const onAbort = () => settle(() => reject(new Error('Upload abgebrochen')));

    xhr.open('PUT', uploadUrl, true);
    // Must match the Content-Type the URL was signed with.
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.addEventListener('progress', (event) => {
      lastActivity = Date.now();
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    xhr.addEventListener('load', () =>
      settle(() =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Upload fehlgeschlagen (HTTP ${xhr.status})`)),
      ),
    );
    xhr.addEventListener('error', () =>
      // A Blob the browser cannot read lands here too, with no detail of its own.
      settle(() => reject(new Error(`Upload fehlgeschlagen. ${STALL_MESSAGE}`))),
    );
    xhr.addEventListener('abort', onAbort);
    xhr.send(file);
  });
}

/**
 * Checks that the file can actually be read, before a job is created for it.
 *
 * One byte is enough: a file the picker only referenced - the Google Drive case -
 * fails here, and failing before the job exists means no row that says
 * "wird hochgeladen" about an upload that never started.
 */
export async function assertReadable(file: File): Promise<void> {
  try {
    await file.slice(0, 1).arrayBuffer();
  } catch {
    throw new Error(`Die Datei lässt sich nicht lesen. ${STALL_MESSAGE}`);
  }
}
